<template>
  <RuesBoxSize
    v-if="props.item?.objekte?.length > 0"
    :size="props.item?.width"
    class="text-left grid grid-cols-1 gap-y-4 product-carousel"
  >
    <UiHeadline
      v-if="props.item?.headline"
      type="h2"
      design="h2"
    >
      {{ props.item?.headline }}
    </UiHeadline>
    <div
      ref="carouselRef"
      class="min-h-[230px] lg:min-h-[210px] xl:min-h-[240px] 1xl:min-h-[240px] 2xl:min-h-[230px] select-none"
    >
      <div
        v-if="!showSkeleton"
        class="relative"
      >
        <UiCarousel
          ref="emblaCarouselRef"
          class="embla-product"
          ignore-drag-inside=".shared-picture-carousel"
          :slide-size="SLIDE_SIZE_MOBILE"
          :slide-count="total"
          :ssr-breakpoints="ssrBreakpoints"
          :show-arrows="total > 1"
          :options="carouselOptions"
          :accessibility="accessibilityOptions"
          @init="onCarouselInit"
        >
          <div
            v-for="article in objekte"
            :key="article.id"
            class="embla__slide w-full px-2 py-3"
          >
            <SharedObjectCard
              :nested-carousel="true"
              design="objectslider"
              :lazy-loading="true"
              :builder-carousel="isOrWasInViewport && !isSmallViewport"
              class="w-full block"
              :item="article"
              :region-distance-to="props.item?.h2_region_ort"
            />
          </div>

          <template
            v-if="total > 1"
            #prev
          >
            <span aria-hidden="true" />
          </template>
          <template
            v-if="total > 1"
            #next
          >
            <span aria-hidden="true" />
          </template>
        </UiCarousel>
      </div>
      <div
        v-show="showSkeleton"
        class="flex flex-nowrap gap-x-4 w-full overflow-hidden"
      >
        <SkeletonSharedObjectCard
          :design="props.item?.element === 'Fotolinks' ? 'objectsliderlight' : 'objectslider'"
          class="w-[75%] md:w-[41.7%] lg:w-[33.3333%]"
        />
        <SkeletonSharedObjectCard
          :design="props.item?.element === 'Fotolinks' ? 'objectsliderlight' : 'objectslider'"
          class="md:w-[41.7%] lg:w-[33.3333%] hidden md:block"
        />
        <SkeletonSharedObjectCard
          :design="props.item?.element === 'Fotolinks' ? 'objectsliderlight' : 'objectslider'"
          class="lg:w-[33.3333%] hidden lg:block"
        />
        <SkeletonSharedObjectCard
          design="objectslidersmall"
          class="w-[22%] md:w-[12.2%] block lg:hidden"
        />
      </div>
    </div>
    <div
      v-if="props.item?.element !== 'Fotolinks' && props.type !== 'ObjektTeaser4'"
      class="text-center md:text-right"
    >
      <div class="flex md:inline-block">
        <UiButton
          href="#"
          color="primary"
          class="w-full md:w-auto"
        >
          {{ t('show_all_offers') }}
        </UiButton>
      </div>
    </div>
    <div
      v-if="props.type === 'ObjektTeaser4'"
      class="text-center md:text-right"
    >
      <div class=" flex md:inline-block">
        <UiMaskedLink
          :href="'/search/' + hashParams"
          :external="true"
        >
          <UiButton
            color="primary"
            class="w-full md:w-auto"
            @click="save"
          >
            {{ t('show_all_offers_headline', { headline: props.item?.headline }) }}
          </UiButton>
        </UiMaskedLink>
      </div>
    </div>
  </RuesBoxSize>
</template>

<script setup lang="ts">
import type { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel'
import type { AccessibilityOptionsType } from 'embla-carousel-accessibility'
import type { SsrOptionsType } from 'embla-carousel-ssr'
import type { ObjektTeaser4ElementData, StartseiteFotolinksElementData } from '~/types/TypesFromPortalBackend'
import type SearchParams from '~/types/search/SearchParams'
import UrlHashSearchParams from '~/utils/search/UrlHashSearchParams'
import { BREAKPOINT_LG_MIN_QUERY } from '~/utils/breakpoints'

const t = useTranslations('RuesProductCarousel').translate

const SLIDE_SIZE_MOBILE = 76.5
const SLIDE_SIZE_TABLET = 43.5
const SLIDE_SIZE_DESKTOP = 33.333
const INITIAL_OBJEKTE = 3

interface Props {
  skeleton?: boolean
  item: ObjektTeaser4ElementData | StartseiteFotolinksElementData
  type?: string
  searchParams?: Partial<SearchParams>
}

const props = withDefaults(defineProps<Props>(), {
  skeleton: true,
  type: '',
  searchParams: () => ({})
})

const loaded = ref(false)
const isEmblaInitialized = ref(false)
const carouselRef: Ref<HTMLElement | null> = ref(null)
const emblaCarouselRef = ref<{ emblaApi?: EmblaCarouselType } | null>(null)

const showSkeleton = computed(() => props.skeleton && !loaded.value)

const objekte = computed(() => {
  const all = [...(props.item?.objekte ?? [])]
  const beforeInit = all.slice(0, INITIAL_OBJEKTE)

  if (isEmblaInitialized.value) {
    return all
  }

  return beforeInit
})

const total = computed(() => objekte.value.length)

const ssrBreakpoints = computed(() => {
  const n = Math.max(total.value, 0)
  return {
    '(min-width: 768px)': { slideSizes: Array.from({ length: n }, () => SLIDE_SIZE_TABLET) },
    [BREAKPOINT_LG_MIN_QUERY]: { slideSizes: Array.from({ length: n }, () => SLIDE_SIZE_DESKTOP) }
  } as NonNullable<SsrOptionsType['breakpoints']>
})

const carouselOptions = computed<EmblaOptionsType>(() => ({
  align: 'start',
  loop: total.value > 1
}))

const accessibilityOptions = computed<AccessibilityOptionsType>(() => ({
  announceChanges: true,
  carouselAriaLabel: props.item?.headline || t.value('carousel_aria_label'),
  previousButtonAriaLabel: t.value('previous_button_aria_label'),
  nextButtonAriaLabel: t.value('next_button_aria_label'),
  slideAriaLabel: (_grouped, first) => t.value('slide_aria_label', { current: first + 1, total: total.value }),
  liveRegionContent: (_grouped, first) => t.value('live_region_content', { current: first + 1, total: total.value }),
  rootNode: (emblaRoot) => emblaRoot.parentElement
}))

const { isOrWasInViewport } = useIntersectionObserver(carouselRef)
const { isMatching: isSmallViewport } = useMediaQuery('(max-width: 479px)')

const searchParamsStore = useSearchParamsStore()
const urlHashSearchParams = new UrlHashSearchParams()
const hashParams: ComputedRef<string> = computed(() => {
  const hashParamsString = urlHashSearchParams.convertSearchParamsToHashParamsString(searchParamsStore.activeFilters)
  if (hashParamsString === undefined || typeof hashParamsString !== 'string') {
    return ''
  }
  return hashParamsString
})

const save = (): void => {
  const mergedParams = {
    ...props.searchParams,
    ...(props.item?.serp_url_params ?? {})
  }
  searchParamsStore.$patch(mergedParams)
}

if (props.item.use_structured_data && useJsonLdItemListOfVacationRentals().getAssembledObject() === null) {
  useJsonLdItemListOfVacationRentals().setProductsList(props.item?.objekte)
}

const onCarouselInit = (_api: EmblaCarouselType): void => {
  isEmblaInitialized.value = true
}

watch(objekte, async () => {
  await nextTick()
  emblaCarouselRef.value?.emblaApi?.reInit()
})

onMounted(() => {
  loaded.value = true
})
</script>

<style src="./css/RuesProductCarousel.css" scoped></style>
